import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Send, ChevronRight, CheckCircle2 } from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "@/data/services";
import { useSiteImages } from "@/lib/siteImages";

const ENQUIRY_URL = process.env.REACT_APP_ENQUIRY_URL;

const JOB_TYPES = [
  "Bathroom Renovation", "Bathroom Repair", "Blocked Drain", "Burst Pipe",
  "Civil Works", "Drainage", "Emergency Plumbing", "General Plumbing",
  "Hot Water System", "Leak Detection", "Pipe Repair", "Tap/Fixture Repair",
  "Toilet Repair", "Other",
];

const fieldClass = "w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors";

const replyText = (v) =>
  typeof v === "string" ? v : Array.isArray(v) ? v.map((e) => e?.msg || String(e)).join(" ") : "";

const honeypotStyle = { position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0, overflow: "hidden" };

const QuoteForm = ({ onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(ENQUIRY_URL, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(e.currentTarget),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) onSuccess(replyText(data.message) || "Thanks — we'll be in touch shortly.");
      else setError(replyText(data.detail) || "Something went wrong. Please call us directly.");
    } catch {
      setError(`Couldn't send right now. Please call us on ${PHONE}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="quote-form">
      <input type="text" name="name" required placeholder="Full Name *" className={fieldClass} data-testid="quote-name" />
      <input type="text" name="suburb" required placeholder="Suburb *" className={fieldClass} data-testid="quote-suburb" />
      <input type="tel" name="phone" required placeholder="Phone Number *" className={fieldClass} data-testid="quote-phone" />
      <input type="email" name="email" placeholder="Email Address (optional)" className={fieldClass} data-testid="quote-email" />
      <select name="service" required defaultValue="" className={fieldClass} data-testid="quote-service">
        <option value="" disabled className="bg-card text-muted">Select Job Type *</option>
        {JOB_TYPES.map((j) => (
          <option key={j} value={j} className="bg-card text-ink">{j}</option>
        ))}
      </select>
      <textarea name="message" required rows={4} placeholder="Describe your project or issue *" className={`${fieldClass} resize-none`} data-testid="quote-message" />
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" defaultValue="" style={honeypotStyle} />
      {error && <div className="text-emergency text-sm" data-testid="quote-error">{error}</div>}
      <button
        type="submit"
        disabled={submitting}
        data-testid="quote-submit"
        className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Send size={16} /> {submitting ? "Sending..." : "Submit Request"}
      </button>
    </form>
  );
};

const Contact = () => {
  const [successMsg, setSuccessMsg] = useState("");
  const { slots } = useSiteImages();

  return (
    <div data-testid="contact-page">
      <section className="relative">
        <div className="relative mx-auto max-w-page px-6 lg:px-10 pt-20 pb-16">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Contact</span>
          </nav>
        </div>
      </section>

      <section id="quote" className="pb-24">
        <div className="mx-auto max-w-page px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Request a Quote</div>
            <h1 className="font-display text-5xl md:text-7xl text-ink mb-6">Get in Touch</h1>
            <p className="text-muted text-lg leading-relaxed mb-10 max-w-md">
              Tell us about your project and we'll provide a detailed quote within 24 hours.
              Emergency services available around the clock.
            </p>
            {slots.contact && (
              <img src={slots.contact} alt="Styne Group" data-testid="contact-photo"
                className="w-full max-w-md aspect-[4/3] object-cover border border-line mb-10" />
            )}
            <div className="space-y-6">
              <a href={PHONE_HREF} className="flex items-start gap-4 group" data-testid="contact-phone">
                <div className="w-11 h-11 border border-line group-hover:border-gold flex items-center justify-center transition-colors">
                  <Phone size={16} className="text-gold" />
                </div>
                <div>
                  <div className="text-muted text-xs tracking-widerx uppercase mb-1">Call Us</div>
                  <div className="font-display text-2xl text-ink group-hover:text-gold transition-colors">{PHONE}</div>
                </div>
              </a>
              <a href={EMAIL_HREF} className="flex items-start gap-4 group" data-testid="contact-email">
                <div className="w-11 h-11 border border-line group-hover:border-gold flex items-center justify-center transition-colors">
                  <Mail size={16} className="text-gold" />
                </div>
                <div>
                  <div className="text-muted text-xs tracking-widerx uppercase mb-1">Email</div>
                  <div className="text-ink group-hover:text-gold transition-colors break-all">{EMAIL}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-line flex items-center justify-center">
                  <MapPin size={16} className="text-gold" />
                </div>
                <div>
                  <div className="text-muted text-xs tracking-widerx uppercase mb-1">Service Area</div>
                  <div className="text-ink">Melbourne & Yarra Valley</div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative lg:col-span-7 bg-card border border-line p-8 md:p-10">
            <div className="font-display text-2xl text-gold tracking-wide mb-8">Fast Quote</div>
            {successMsg ? (
              <div className="py-16 text-center" data-testid="contact-success">
                <CheckCircle2 size={48} className="text-gold mx-auto mb-5" />
                <h3 className="font-display text-3xl text-ink mb-3">Request Received</h3>
                <p className="text-muted max-w-md mx-auto" data-testid="contact-success-message">{successMsg}</p>
                <p className="text-muted text-sm mt-4">
                  For emergencies, call <a href={PHONE_HREF} className="text-gold">{PHONE}</a>.
                </p>
              </div>
            ) : (
              <QuoteForm onSuccess={setSuccessMsg} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
